/* ==========================================================================

 ShowHide View

 Description: Generic, efficient ShowHide view that'll hopefully replace the
 various techniques used across the site, used on LoginView.

 Check unit tests for usage examples

 ========================================================================== */

'use strict';

var _ = require('underscore');
var mediator = require('../mediator');
var $global = require('../globalElements');
var Backbone = require('backbone');
import {getLogger} from 'loglevel';
const log = getLogger('ShowHide');
import $ from 'jquery';

class ShowHide extends Backbone.View.extend({
  hideOthers: true, // true = when an item is shown, it'll hide other/sibling elements, false = it'll just toggle itself, ignoring others
  group: null, // value to group dropdowns to only have one open at a time
  activeBodyClass: null, // Class to be added to the body when dropdown is active
  activeClass: 'is-selected',
  type: '[data-toggle-show-hide]',
  animation: 'show',
  legacy: false,
  hoverDelay: [200, 0], // hover in/hover out in ms, hover out delay should be longer/equal to in to avoid odd effects
  elementAttribute: 'data-toggle-show-hide',
  justChildren: false, // if true, uses .children() to find elements to show/hide rather than find, meaning a specific markup structure is required (the hide/show container has to be an immediate child of this.$el)
  animations: {
    show: ['show', 'hide'],
    slide: ['slideDown', 'slideUp'],
    fade: ['fadeIn', 'fadeOut']
  },
  events: {
    'change input[type=radio]': 'radioEvent', // baked in radio button stuff as it's the most likely use case
    'click [data-toggle-show-hide]': 'dataEvent', // generic data attribute that can be used on any element
    'click button': 'buttonEvent'
  }
}) {

  initialize(options) {
    this.setup(options);
  }

  setup(options) {
    _.extend(this, options);
    this.id = this.id || this.$el.attr('class');
    this.animationStyle = this.animations[this.animation];
    this.methodSetup();
    if (this.legacy) {
      this.legacySetup();
    } else {
      this.setupItems(this.type);
    }
    this.setupMediator();
  }

  // currently only one 'custom' method, should probably move legacy events into here
  methodSetup() {
    switch (this.method) {
      case 'hover':
        this.setupHoverEvents();
        break;
      case 'keydown':
        this.setupKeyEvents();
        break;
      case 'focus':
        this.setupFocusEvents();
        break;
      default:
        return false;
    }
  }

  // adds event listeners that don't follow the data-toggle-show-hide convention
  legacySetup() {
    this.events['click ' + this.legacy.clickElements] = 'legacyEvent';
    this.delegateEvents();
    this.setupItems(this.legacy.clickElements);
  }

  // shows items based on whatever selector has been specified in the legacy object
  legacyEvent(e) {
    if (!this.legacy) return;
    // clickDisplayArray, so if you click on the third tab, it'll display the third item in the this.items array
    // if id/class/data attribute values are liable to change, eg on translation, this offers a way around in lieu of a BE fix
    if (this.legacy.clickDisplayArray) {
      this.showItems(this.items[e.target.attributes['data-hide-show-array'].value]);
    } else {
      try {
        let attributeName = this.removeDataBrackets(this.legacy.clickElements);
        let attribute = e.currentTarget.attributes[attributeName];
        let id = attribute.value;
        this.showItems(this.getItem(id));
      } catch (err) {
        log.warn('Could not find item from attribute', this, e);
      }
    }
  }

  // don't know the selector generally so easier to setup in a function vs the events object
  setupHoverEvents() {
    this.events['mouseenter ' + this.type] = 'enterEvent';
    this.events['mouseleave ' + this.type] = 'leaveEvent';
    this.$el.on('mousemove.ShowHide', this.type, this.moveEvent.bind(this));
    this.delegateEvents();
  }

  setupFocusEvents() {
    //this.events['focus ' + this.type] = 'focusEvent';
    //this.events['blur ' + this.type] = 'blurEvent';
    this.delegateEvents();
  }
  setupKeyEvents() {
    this.events['keydown ' + this.type] = 'keydownEvent';
    this.delegateEvents();
  }
  // can show/hide an element based on an external event
  setupMediator() {
    mediator.on('SHOWHIDE_SHOW:' + this.id, function(options) {
      var id = options.id || this.getElementId(options.$element[0]);
      this.toggleItem(this.getItem(id));
    }, this);


    // If we have a group set for only one dropdown to show, let's setup mediator handler
    if (this.group) {
      mediator.on('SHOWHIDE_HIDE:Group' + this.group, function() {
        this.hideItems(this.items);
      }, this);
    }
  }

  // sets up objects that'll be shown/hidden, avoiding lookups post initialise
  setupItems(type) {
    switch (type) {
      case 'simpleClose':
        this.setupSimpleClose();
        break;
      default:
        this.triggers = this.$el.find(type);
        this.buildItemArray(this.triggers);
        this.setupChecked();
    }
  }

  // simplified setup if you just want to display an item with a single hide button
  setupSimpleClose() {
    this.item = { $elements: this.$el };
    if (this.forceShow) {
      this.toggleAction(this.item, true);
    }
  }

  // constructs the arrays of items to show/hide, a item triggering a show/hide will reference a position in the this.items array
  buildItemArray(elements) {
    this.items = [];
    _.each(elements, function(item, i) {
      var showThis = item.getAttribute('data-toggle-show-hide');
      var id = !showThis ? !item.id ? i : item.id : showThis;
      item.setAttribute('data-hide-show-array', i);
      this.items.push({ id: id, $elements: this.findElements(id), trigger: item, display: false });
    }, this);
  }

  // returns array of elements to add to items array
  findElements(id) {
    if (this.displayChild) {
      return this.$el.find(this.displayChild).eq(id);
    }
    return !this.legacy ? (!this.justChildren ? this.$el.find('.' + id) : this.$el.children('.' + id)) : this.$el.find(this.legacy.clickDisplayArray).eq(id);
  }

  // should probably live in a separate utility, but necessary in order to look up item based on data name
  removeDataBrackets(str = '') {
    return str.replace(/[\[\]']+/g, '');
  }

  // if a radio button is checked, will display associated content
  setupChecked() {
    _.each(this.triggers, function(item) {
      if (item.checked) {
        this.showItems(this.getItem(item.id));
      }
    }, this);
  }

  // fires when a cursor enters an element
  enterEvent(e) {
    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout);
      this.enterTimeout = null;
    }
    this.trackedMouseMove = true;
    var delay = this.active ? 0 : this.hoverDelay[0];
    this.enterTimeout = setTimeout(_.bind(function() {
      if (!this.active && this.onEnter) {
        this.onEnter(e); // callback onEnter fires when the cursor enters the parent element (eg. the UL rather than the individual LIs)
      }
      this.active = true;
      if (!this.disabled) {
        this.showItems(this.items[e.currentTarget.attributes['data-hide-show-array'].value]);
      }
      this.enterTimeout = null;
    }, this), delay);
  }

  moveEvent(e) {
    this.$el.off('mousemove.ShowHide');
    if (!this.trackedMouseMove) {
      this.enterEvent(e);
    }
  }

  // fires when a cursor leaves an element
  leaveEvent(e) {
    this.trackedMouseMove = true;
    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout);
      return false;
    }
    setTimeout(_.bind(function() {
      if (!this.enterTimeout && this.onLeave) {
        this.onLeave(e); // when you're leaving the parent element, this fires (useful for triggering the background darkening event)
      }
      this.active = false;
      this.hideItems(this.items);
    }, this), this.hoverDelay[1]);
  }

  // radio event skips out the toggle functionality, doesn't seem necessary as you don't unselect a radio
  radioEvent(e) {
    this.showItems(this.getItem(e.currentTarget.id));
  }

  // can potentially hide/show itself depending on whether this.getItem(id).display is true
  dataEvent(e) {
    e.preventDefault();
    var id = this.getElementId(e.currentTarget);
    var item = this.getItem(id);
    if (item) {
      this.toggleItem(item);
    }
  }

  buttonEvent(e) {
    if (this.type === 'simpleClose') {
      e.preventDefault();
      this.toggleAction(this.item, false);
    }
  }

  getElementId(el) {
    return el.attributes[this.elementAttribute].value;
  }

  // will hide itself (only ever needs to hide itself, so doesn't need to call hideItems), or show items
  toggleItem(item) {
    if (item.display) {
      // Add the active body class if one was set
      $global.$body.removeClass(this.activeBodyClass);
      this.toggleAction(this.getItem(item.id), false);
    } else {
      // Add the active body class if one was set. ( Used for Overlays, styles, etc... )
      $global.$body.addClass(this.activeBodyClass);
      this.showItems(item);
    }
  }

  // returns an item from the this.items array based on the id attribute
  getItem(id) {
    return _.find(this.items, { id: id });
  }

  // gets all items that don't contain a particular id
  getOtherItems(id) {
    return _.filter(this.items, function(o) {
      return o.id !== id;
    });
  }

  // shows associated items, hiding other items if flag has been set
  showItems(item) {
    this.toggleAction(item, true);
    if (this.hideOthers) {

      this.hideItems(this.getOtherItems(item.id));
    }
  }

  // loops through the items that need to be hidden
  hideItems(toHide) {
    _.each(toHide, function(item) {
      this.toggleAction(item, false);
    }, this);
  }

  // toggling an item (hiding/showing)!
  toggleAction(item, toggle) {

    // We are triggering the show event, lets trigger a group mediator if we have one
    if (toggle && this.group) {
      mediator.trigger('SHOWHIDE_HIDE:Group' + this.group, {});
    }

    if (item.trigger) {
      // Since we can fall down the rabbit hole of toggling class and they become out of sync,
      // We assign an action value from a tuple
      var action = (toggle ? 'add' : 'remove');
      item.trigger.classList[action](this.activeClass);
    }

    this.animateStyle(toggle, item);
    item.display = toggle;

    var callback = toggle ? 'onShow' : 'onHide';
    if (this[callback]) {
      this[callback]();
    }
  }

  // hides/shows/animates the $elements
  animateStyle(show, item) {
    show = show ? this.animationStyle[0] : this.animationStyle[1];
    item.$elements[show]();
  }
  // fires when focus an element
  focusEvent(e) {
    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout);
      this.enterTimeout = null;
    }
    this.trackedMouseMove = true;
    var delay = this.active ? 0 : this.hoverDelay[0];
    this.enterTimeout = setTimeout(_.bind(function() {
      if (!this.active && this.onEnter) {
        this.onEnter(e); // callback onEnter fires when the cursor enters the parent element (eg. the UL rather than the individual LIs)
      }
      this.active = true;
      if (!this.disabled) {
        this.showItems(this.items[e.currentTarget.attributes['data-hide-show-array'].value]);
      }
      this.enterTimeout = null;
    }, this), delay);
  }

  // fires when blur an element
  blurEvent(e) {
    this.trackedMouseMove = true;
    if (this.enterTimeout) {
      clearTimeout(this.enterTimeout);
      return false;
    }
    const currentTarget = e.currentTarget;
    setTimeout(_.bind(function() {
      var conatainer = currentTarget.parentNode;
      if (conatainer && this.isTargetInside(conatainer,document.activeElement))
        return;
      if (!this.enterTimeout && this.onLeave) {
        this.onLeave(e); // when you're leaving the parent element, this fires (useful for triggering the background darkening event)
      }
      this.active = false;
      this.hideItems(this.items);
    }, this), this.hoverDelay[1]);
  }

  isTargetInside(parent,target)
  {
    var childrens = parent.getElementsByTagName('*');
    for (var i = 0; i < childrens.length; i++) {
      if (childrens[i] === target)
        return true;
      this.isTargetInside(childrens[i],target)
    }
    return false;
  }
  keydownEvent(e)
  {
    var keyCode = e ? (e.which ? e.which : e.keyCode) : event.keyCode;
    if (keyCode === 13) {
      if (e.target.classList.contains('arrowdownKey')) {
        this.focusEvent(e);
        $('.arrowdown').hide();
        $('.arrowup').show();
      }
      else if (e.target.classList.contains('arrowupKey'))
      {
        this.blurEvent(e);
        $('.arrowdown').show();
        $('.arrowup').hide();
      }
      else
      {
        return;
      }
    }
  }
}

module.exports = ShowHide;



// WEBPACK FOOTER //
// ./assets/js/components/ShowHide.js